/**
 * Listens to the microphone that is already being recorded, and reports
 * whether anything is actually coming through it.
 *
 * Read-only: it taps the SAME stream the recorder encodes rather than opening
 * its own, so it can never be the reason a second `getUserMedia` prompt
 * appears, and it measures the exact signal that ends up in the file.
 *
 * The thresholds live in `micLevel.ts`. See there for why a live track is not
 * evidence of a working microphone, and why the meter's idea of "heard" is
 * not the one the exam reports on.
 */
import { useEffect, useRef, useState } from "react";
import { dead, heard, meterWidth, rmsOf } from "./micLevel";

export interface MicLevel {
  /** RMS of the last sample, 0-1. Zero when there is no microphone. */
  level: number;
  /** Whether anything has been heard since this stream was attached. */
  everHeard: boolean;
  /** Since the last sample above the meter threshold; null before listening. */
  silentForMs: number | null;
  /** Since the last sample above `DEAD_RMS`; null before listening. */
  deadForMs: number | null;
  /**
   * Whether the tap is actually running. A suspended AudioContext returns a
   * flat line that is indistinguishable from a muted microphone, so anything
   * that draws a conclusion from silence has to check this first.
   */
  listening: boolean;
}

const IDLE: MicLevel = {
  level: 0,
  everHeard: false,
  silentForMs: null,
  deadForMs: null,
  listening: false,
};

/** ~16 fps: smooth enough to read as a live meter, cheap enough to run. */
const SAMPLE_MS = 60;

export interface UseMicLevelOptions {
  /**
   * Whether `level` is published on every sample.
   *
   * The exam runner does NOT want it. It needs only the silence verdict, and
   * publishing a new level sixteen times a second from the top of the page
   * re-renders the whole paper — on a machine already encoding two video
   * streams — for a bar nobody is looking at. With this off, state changes
   * only when the verdict does.
   */
  meter?: boolean;
}

export function useMicLevel(
  stream: MediaStream | null,
  enabled = true,
  { meter = true }: UseMicLevelOptions = {},
): MicLevel {
  const [state, setState] = useState<MicLevel>(IDLE);
  // Read inside the sampling loop, which is created once per stream: state
  // read there would be the value captured when the loop started.
  const heardRef = useRef(false);

  useEffect(() => {
    const track = stream?.getAudioTracks()[0];
    if (!enabled || !track || track.readyState !== "live") {
      heardRef.current = false;
      setState(IDLE);
      return;
    }

    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    // No Web Audio: the meter simply does not appear. It is a diagnostic, and
    // a diagnostic must never be the thing that breaks setup.
    if (!Ctor) return;

    const ctx = new Ctor();
    // A context created without a user gesture starts suspended, and a
    // suspended analyser reads a working microphone as a flat line forever.
    // Chrome resumes on the next gesture; Safari and Firefox do not, which is
    // why `listening` is reported rather than assumed.
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);

    const analyser = ctx.createAnalyser();
    // Small window: this is a level meter, not a spectrum, and a short frame
    // keeps the bar responsive to the syllable rather than the sentence.
    analyser.fftSize = 512;
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(analyser);
    // Deliberately NOT connected to the destination — routing the microphone
    // to the speakers is a feedback loop in a room with no headphones.

    const samples = new Uint8Array(analyser.fftSize);
    let lastHeardAt: number | null = null;
    let lastLoudAt: number | null = null;
    let startedAt = performance.now();

    /**
     * A hidden tab is throttled and may not sample at all, so time spent
     * hidden is not evidence of a silent room — the window restarts rather
     * than counting sleep as silence.
     */
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      startedAt = performance.now();
      lastHeardAt = null;
      lastLoudAt = null;
    };
    document.addEventListener("visibilitychange", onVisibility);

    const tick = () => {
      const now = performance.now();

      // A suspended tap reads a flat line whatever the microphone is doing,
      // so time spent suspended is not evidence of silence. `listening` alone
      // would gate the REPORT while the counter kept running underneath —
      // and the moment a gesture resumed the context, a window measured
      // entirely while deaf would be published as ten minutes of silence on a
      // microphone that had just been listened to for 60 ms.
      if (ctx.state !== "running") {
        startedAt = now;
        lastHeardAt = null;
        lastLoudAt = null;
        setState((prev) =>
          prev.listening || prev.level !== 0
            ? { ...IDLE, everHeard: heardRef.current }
            : prev,
        );
        return;
      }

      analyser.getByteTimeDomainData(samples);
      const level = rmsOf(samples);
      if (heard(level)) {
        lastHeardAt = now;
        heardRef.current = true;
      }
      if (!dead(level)) lastLoudAt = now;

      const next: MicLevel = {
        level,
        everHeard: heardRef.current,
        silentForMs: now - (lastHeardAt ?? startedAt),
        deadForMs: now - (lastLoudAt ?? startedAt),
        listening: true,
      };

      setState((prev) => (changed(prev, next, meter) ? next : prev));
    };

    const timer = window.setInterval(tick, SAMPLE_MS);
    tick();

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      source.disconnect();
      // Every AudioContext holds an audio device open; one per setup visit
      // adds up to a browser that will not open any more of them. `close` on
      // an already-closed context throws synchronously, and a throw here
      // escapes React's cleanup and takes the page with it.
      try {
        void ctx.close().catch(() => undefined);
      } catch {
        /* already closed */
      }
    };
  }, [stream, enabled, meter]);

  return state;
}

/**
 * Whether this sample is worth a re-render.
 *
 * Without `meter`, only the verdict matters, and the verdict changes rarely —
 * which is what keeps this hook off the exam runner's render path.
 */
function changed(prev: MicLevel, next: MicLevel, meter: boolean): boolean {
  if (prev.everHeard !== next.everHeard || prev.listening !== next.listening) {
    return true;
  }

  // These are thresholds someone crosses, not counters anyone watches tick.
  // Rounded coarsely — and much more coarsely with no meter, where the only
  // reader is a ten-minute silence check on a page that must not re-render
  // for nothing.
  const step = meter ? 1000 : 30_000;
  if (
    Math.floor((prev.deadForMs ?? -1) / step) !==
    Math.floor((next.deadForMs ?? -1) / step)
  ) {
    return true;
  }
  if (
    meter &&
    Math.floor((prev.silentForMs ?? -1) / 1000) !==
      Math.floor((next.silentForMs ?? -1) / 1000)
  ) {
    return true;
  }
  // The bar itself, only for whoever is drawing one, and only when it would
  // visibly move.
  return (
    meter &&
    Math.round(meterWidth(prev.level) * 40) !==
      Math.round(meterWidth(next.level) * 40)
  );
}
